import { useCallback, useEffect, useState } from 'react';
import { Globe2, Loader2, Lock, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../api/client.js';

/**
 * DNS → Domains (2026-10-23): every domain of every Spaceship account in one
 * table — expiry + days left (amber < 30, red < 7), auto-renew, where its DNS
 * is served (from the nameservers) and what in the panel uses it. A domain
 * whose DNS is at Spaceship opens its records (list / add / edit / delete);
 * Spaceship's own records are shown locked. Every write is read back by the
 * backend — the result line says whether it was confirmed.
 */

const DNS_AT = {
  spaceship: { label: 'Spaceship', cls: 'bg-s-blue text-[#2E5C8A] border-bd-blue' },
  cloudflare: { label: 'Cloudflare', cls: 'bg-warn-bg text-[#8B5206] border-warn-bd' },
  other: { label: 'other', cls: 'bg-s-3 text-t2 border-bd' },
  unknown: { label: 'unknown', cls: 'bg-s-3 text-t3 border-bd' },
};
const USED_BY = { wp: 'WP', static: 'Static', mail: 'Mail' };

export function DomainsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // domain name whose records are open

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      setData(await api(`/api/dns/domains${refresh ? '?refresh=1' : ''}`));
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const failed = data?.accounts.filter((a) => !a.ok) ?? [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-t3">
          {data ? `${data.count} domain(s) on ${data.accounts.length} account(s)` : 'Domains on your Spaceship accounts'}
          {data?.expiring_soon > 0 && <span className="ml-2 font-medium text-[#8B5206]">· {data.expiring_soon} expire in &lt; 30 days</span>}
        </p>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {error && <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">{error}</div>}
      {failed.map((a) => (
        <div key={a.id} className="bg-warn-bg border border-warn-bd text-[#8B5206] text-xs rounded-md px-3 py-2">
          Account “{a.label}” could not be read ({a.detail ?? a.error}) — its domains are not listed, not “none”.
        </div>
      ))}
      {data?.used_by_errors?.map((e) => <p key={e} className="text-xs text-[#8B5206]">{e}</p>)}

      {data === null ? (
        !error && <p className="text-sm text-t3">Loading…</p>
      ) : data.domains.length === 0 ? (
        <div className="bg-white border border-bd rounded-lg p-8 text-center text-sm text-t3">
          <Globe2 className="w-8 h-8 mx-auto mb-3 text-ic-light" />
          No domains — add a Spaceship account under Accounts.
        </div>
      ) : (
        <div className="bg-white border border-bd rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-3 py-2">Domain</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Auto-renew</th>
                <th className="px-3 py-2">DNS at</th>
                <th className="px-3 py-2">Used by</th>
                <th className="px-3 py-2 text-right">Records</th>
              </tr>
            </thead>
            <tbody>
              {data.domains.map((d) => (
                <DomainRow key={d.name} d={d} open={open === d.name} onToggle={() => setOpen(open === d.name ? null : d.name)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DomainRow({ d, open, onToggle }) {
  const at = DNS_AT[d.dns_at] ?? DNS_AT.unknown;
  const managed = d.dns_at === d.provider;
  return (
    <>
      <tr className="border-b border-bd last:border-0 align-top">
        <td className="px-3 py-2">
          <div className="font-medium text-t1">{d.name}</div>
          <div className="text-[11px] text-t3">{d.account_label}</div>
        </td>
        <td className="px-3 py-2 text-xs whitespace-nowrap"><DaysLeft date={d.expiration_date} days={d.days_left} /></td>
        <td className="px-3 py-2 text-xs">
          {d.auto_renew === null ? '—' : d.auto_renew ? <span className="text-ok">on</span> : <span className="text-t3">off</span>}
        </td>
        <td className="px-3 py-2">
          <span title={(d.nameservers ?? []).join(', ')} className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${at.cls}`}>{at.label}</span>
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {d.used_by.length === 0 ? <span className="text-xs text-t3">—</span> : d.used_by.map((u) => (
              <span key={`${u.kind}-${u.name}`} title={u.detail ?? ''} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-s-3 border border-bd text-[11px] text-t2">
                <span className="font-semibold">{USED_BY[u.kind]}</span> <span className="font-mono">{u.name}</span>
              </span>
            ))}
          </div>
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {managed ? (
            <button type="button" onClick={onToggle}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue text-blue hover:bg-s-blue">
              {open ? 'Close' : 'Records'}
            </button>
          ) : (
            <span className="text-[11px] text-t3">DNS at {d.dns_at === 'cloudflare' ? 'Cloudflare' : 'other'} — not managed here yet</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-bd bg-s-2">
          <td colSpan={6} className="px-3 py-3"><RecordsPanel accountId={d.account_id} zone={d.name} /></td>
        </tr>
      )}
    </>
  );
}

function DaysLeft({ date, days }) {
  if (!date || days === null) return <span className="text-t2">—</span>;
  const when = new Date(date).toLocaleDateString();
  if (days < 0) return <span className="text-err font-medium">{when} (expired)</span>;
  const cls = days < 7 ? 'text-err font-medium' : days < 30 ? 'text-[#8B5206] font-medium' : 'text-t2';
  return <span className={cls}>{when} · {days} days left</span>;
}

// ─── Records of one zone ────────────────────────────────────────────────────

const TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX'];
const EMPTY = { type: 'A', name: '@', value: '', priority: '10', ttl: '1800' };

function RecordsPanel({ accountId, zone }) {
  const base = `/api/dns/accounts/${accountId}/domains/${encodeURIComponent(zone)}/records`;
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // null | { editing: record|null, values }
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const load = useCallback(async () => {
    try {
      setRecords((await api(base)).records);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const describe = (err, need) => (err.status === 403 ? `You need ${need} on this DNS account.` : (err.body?.detail ?? err.body?.error?.detail ?? err.message));

  async function write(method, body, need) {
    setBusy(true);
    setOutcome(null);
    try {
      const r = await api(base, { method, body });
      setOutcome({ ok: r.ok, text: r.ok ? 'Saved — read back from Spaceship and confirmed.' : 'Not confirmed by the read-back.', r });
      setForm(null);
    } catch (err) {
      const r = err.body;
      setOutcome({ ok: false, text: r?.mismatches?.length ? `Not confirmed: ${r.mismatches.join('; ')}` : describe(err, need), r });
    } finally {
      setBusy(false);
      load();
    }
  }

  function submit(e) {
    e.preventDefault();
    const v = form.values;
    const rec = { type: v.type, name: v.name.trim() || '@', value: v.value.trim(), ttl: Number(v.ttl) || 1800, ...(v.type === 'MX' ? { priority: Number(v.priority) } : {}) };
    write('POST', { add: [rec], ...(form.editing ? { replace: form.editing } : {}) }, 'Use');
  }

  function remove(r) {
    const line = `${r.type} ${r.name} ${r.type === 'MX' ? `${r.priority} ` : ''}${r.value}`;
    if (!window.confirm(`Delete this record from ${zone}?\n\n${line}\n\nAnything relying on it stops resolving.`)) return;
    write('DELETE', { records: [r] }, 'Full');
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, values: { ...f.values, [k]: e.target.value } }));
  const input = 'rounded-md border border-bd-strong px-2 py-1 text-xs';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-t1">Records · {zone}</div>
        <button type="button" onClick={() => setForm({ editing: null, values: EMPTY })}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue text-white hover:bg-blue-deep">
          <Plus className="w-3.5 h-3.5" /> Record
        </button>
      </div>
      {error && <div className="bg-err-bg border border-err-bd text-err text-xs rounded-md px-3 py-2">{error}</div>}
      {outcome && <p className={`text-xs ${outcome.ok ? 'text-ok' : 'text-err'}`}>{outcome.text}</p>}

      {form && (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2 bg-white border border-bd rounded-md p-2">
          <label className="text-[11px] text-t3">Type
            <select value={form.values.type} onChange={set('type')} disabled={!!form.editing} className={`${input} block bg-white`}>
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-t3">Name
            <input value={form.values.name} onChange={set('name')} className={`${input} block font-mono w-36`} placeholder="@" />
          </label>
          {form.values.type === 'MX' && (
            <label className="text-[11px] text-t3">Priority
              <input value={form.values.priority} onChange={set('priority')} className={`${input} block w-16`} />
            </label>
          )}
          <label className="text-[11px] text-t3 flex-1 min-w-[12rem]">Value
            <input value={form.values.value} onChange={set('value')} required className={`${input} block w-full font-mono`} />
          </label>
          <label className="text-[11px] text-t3">TTL
            <input value={form.values.ttl} onChange={set('ttl')} className={`${input} block w-20`} />
          </label>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-blue text-white disabled:opacity-50">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {form.editing ? 'Save' : 'Add'}
          </button>
          <button type="button" onClick={() => setForm(null)} className="text-xs px-2 py-1.5 text-t3">Cancel</button>
        </form>
      )}

      {records === null ? (
        !error && <p className="text-xs text-t3">Loading records…</p>
      ) : (
        <div className="bg-white border border-bd rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-2 py-1.5">Type</th>
                <th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Value</th>
                <th className="px-2 py-1.5">TTL</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-center text-t3">No records.</td></tr>}
              {records.map((r) => (
                <tr key={`${r.type}-${r.name}-${r.priority}-${r.value}`} className="border-b border-bd last:border-0 align-top">
                  <td className="px-2 py-1.5 font-mono font-semibold text-t2">{r.type}</td>
                  <td className="px-2 py-1.5 font-mono">{r.name}</td>
                  <td className="px-2 py-1.5 font-mono break-all">{r.type === 'MX' ? `${r.priority} ` : ''}{r.value}</td>
                  <td className="px-2 py-1.5 text-t3">{r.ttl ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    {r.locked ? (
                      <span className="inline-flex items-center gap-1 text-t3" title="Spaceship manages this record itself"><Lock className="w-3 h-3" /> Spaceship's own</span>
                    ) : !r.editable ? (
                      <span className="text-t3">edit at Spaceship</span>
                    ) : (
                      <>
                        <button type="button" title="Edit" aria-label={`Edit ${r.type} ${r.name}`} disabled={busy}
                          onClick={() => setForm({ editing: r, values: { type: r.type, name: r.name, value: r.value, priority: String(r.priority ?? 10), ttl: String(r.ttl ?? 1800) } })}
                          className="rounded p-1 text-ic hover:bg-s-3 hover:text-t1"><Pencil className="w-3.5 h-3.5" /></button>
                        <button type="button" title="Delete" aria-label={`Delete ${r.type} ${r.name}`} disabled={busy} onClick={() => remove(r)}
                          className="rounded p-1 text-ic hover:bg-err-bg hover:text-err"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
